import { EEventName, EUserStatus } from '@xeppt/xeppt-sdk/types';
import {
    notificationsByType,
    notificationsGroups,
    notificationsGroupsSettings,
    EEventNameWalletPurchase,
    EEventNameWalletPurchaseRefunded,
    EEventNameWalletSaleReceived,
    EEventNameWalletSaleRefunded
} from '@const/notifications';
import {
    ENotificationCategory,
    ENotificationGroup,
    TNotification
} from '@xeppt/xeppt-sdk/types/notification';
import { TNotificationSettings } from '@xeppt/xeppt-sdk/types/user';
import { NavigateFunction } from 'react-router-dom';
import { ENewNotificationType } from '@enum';
import { prettifyDate } from '@utils/date';
import { TIconType } from '@components/icons';
import { TNotificationAction } from '@types';
import { routes } from '@const/routes';

export const getNotificationTypeByEvent = (event: EEventName) => {
    for (const [type, events] of Object.entries(notificationsByType)) {
        //@ts-ignore
        if (events.includes(event)) {
            return type as ENewNotificationType;
        }
    }
    return undefined; //
};

export const getNotificationGroupByEvent = (event: EEventName) => {
    for (const [type, events] of Object.entries(notificationsGroups)) {
        // @ts-ignore
        if (events.includes(event)) {
            return type as ENotificationCategory;
        }
    }
    return undefined; //
};

// Returns the settings groups to render for a category, restricted to whatever
// the backend response actually contains. The backend now wallet-scopes groups
// (e.g. only individuals get "purchases", only business wallets get "sales"),
// so intersecting the static category → group map against the real response
// keys is what makes wallet-type scoping automatic on the FE — no client-side
// user-type branching needed here.
export const getVisibleNotificationGroups = (
    category: ENotificationCategory,
    notifications: TNotificationSettings | undefined
): ENotificationGroup[] => {
    if (!notifications) {
        return [];
    }
    const groupsForCategory = notificationsGroupsSettings[category] ?? [];
    return groupsForCategory.filter((group) => group in notifications);
};

// Graceful fallback label for a settings group that has no i18n entry yet —
// turns "gift_cards" into "Gift Cards" instead of showing the raw group key.
export const humanizeNotificationGroupKey = (key: string): string => {
    return key
        .split('_')
        .filter(Boolean)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ');
};

export const getNotificationAction = (
    notification: TNotification,
    navigate: NavigateFunction,
    handleGetVerificationResult: (codes: string[], status: EUserStatus) => void
): {
    isSupport?: boolean;
    isViewLimits?: boolean;
    isTransaction?: boolean;
    isRequest?: boolean;
    actions: TNotificationAction[];
} => {
    switch (notification.event) {
        case EEventName.UserUnVerified:
            //@ts-ignore
            if (notification?.payload?.codes && notification?.payload?.codes?.length > 0) {
                return {
                    isSupport: true,
                    actions: [
                        {
                            label: 'View details',
                            onClick: () =>
                                handleGetVerificationResult(
                                    (notification?.payload?.codes as string)
                                        ?.split(',')
                                        .filter(Boolean) || [],
                                    EUserStatus.UNVERIFIED
                                ),
                            variant: 'primary'
                        }
                    ]
                };
            } else {
                return {
                    actions: []
                };
            }
        case EEventName.UserVerified:
            //@ts-ignore
            if (notification?.payload?.codes && notification?.payload?.codes?.length > 0) {
                return {
                    isSupport: true,
                    actions: []
                };
            } else {
                return {
                    actions: []
                };
            }
        case EEventName.CardBlocked:
            return {
                isSupport: true,
                actions: [
                    {
                        label: 'View card',
                        onClick: () =>
                            navigate(`${routes.xeppt_cards}/${notification?.payload?.card_id}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.CardActivated:
        case EEventName.CardCreated:
        case EEventName.CardReplaced:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'View card',
                        onClick: () =>
                            navigate(`${routes.xeppt_cards}/${notification?.payload?.card_id}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.CardBalanceLoaded:
            return {
                isSupport: false,
                isTransaction: true,
                actions: []
            };
        case EEventName.CardTransactionProcessed:
        case EEventName.CardTransactionDeclined:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'View card transactions',
                        onClick: () =>
                            navigate(`${routes.xeppt_cards}/${notification?.payload?.card_id}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.EFTTransferProcessed:
        case EEventName.EFTTransferFailed:
        case EEventName.ETransferProcessed:
        case EEventName.BillPaymentProcessed:
        case EEventName.EPSDepositProcessed:
        case EEventName.PaymentReceived:
        case EEventName.ExternalUserReceiveMoney:
        // eslint-disable-next-line no-fallthrough -- string-literal EEventName consts (promote to @xeppt/xeppt-sdk at next publish)
        case EEventNameWalletPurchase:
        case EEventNameWalletPurchaseRefunded:
        case EEventNameWalletSaleReceived:
        case EEventNameWalletSaleRefunded:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'View transaction details',
                        onClick: () =>
                            navigate(
                                `${routes.transactions}?id=${notification?.payload?.transaction_id}`
                            ),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.CardPinChanged:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'View card',
                        onClick: () =>
                            navigate(`${routes.xeppt_cards}/${notification?.payload?.card_id}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.CardCanceled:
            return {
                isSupport: true,
                actions: [
                    {
                        label: 'View card',
                        onClick: () =>
                            navigate(`${routes.xeppt_cards}/${notification?.payload?.card_id}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.CardFrozen:
        case EEventName.CardUnfrozen:
            return {
                isSupport: true,
                actions: [
                    {
                        label: 'Go to card',
                        onClick: () =>
                            navigate(`${routes.xeppt_cards}/${notification?.payload?.card_id}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.NewEmailAdded:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to profile settings',
                        onClick: () => navigate(`${routes.settings_profile}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.NewPhoneAdded:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to profile settings',
                        onClick: () => navigate(`${routes.settings_profile}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.PrimaryEmailChanged:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to profile settings',
                        onClick: () => navigate(`${routes.settings_profile}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.PrimaryPhoneChanged:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to profile settings',
                        onClick: () => navigate(`${routes.settings_profile}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.BillingAddressChanged:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to profile settings',
                        onClick: () => navigate(`${routes.settings_profile}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.BankAccountLinked:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to Bank accounts',
                        onClick: () =>
                            navigate(
                                `${routes.manage_accounts}?id=${notification?.payload?.eft_account_id}`
                            ),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.BankAccountUnlinked:
            return {
                isSupport: true,
                actions: []
            };
        case EEventName.EPSCardLinked:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to Bank accounts',
                        onClick: () =>
                            navigate(
                                `${routes.manage_accounts}?id=${notification?.payload?.eps_card_id}`
                            ),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.IntercaEmailUpdated:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to <i>Interac</i> Autodeposit®',
                        onClick: () => navigate(`${routes.send_and_request_autodeposit}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.SupportTicketSolved:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Go to Help center',
                        onClick: () => navigate(`${routes.help_center}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.SupportTicketNewMessage:
        case EEventName.SupportTicketStatusChanged:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'View request',
                        onClick: () =>
                            navigate(
                                `${routes.help_center}?id=${notification?.payload?.ticket_id}`
                            ),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.ScheduleBillProcessed:
        case EEventName.ScheduleEFTProcessed:
        case EEventName.ScheduleTransferProcessed:
            return {
                isSupport: false,
                isTransaction: true,
                actions: []
            };
        case EEventName.ScheduleTransferFailed:
        case EEventName.ScheduleEFTFailed:
        case EEventName.ScheduleBillFailed:
            return {
                isSupport: true,
                isTransaction: true,
                actions: []
            };
        case EEventName.LoyaltyDowngradedGold:
        case EEventName.LoyaltyDowngradedSilver:
        case EEventName.LoyaltyUpgradedGold:
        case EEventName.LoyaltyUpgradedPlatinum:
        case EEventName.LoyaltyFirstReviewReminderGold:
        case EEventName.LoyaltyFirstReviewReminderPlatinum:
        case EEventName.LoyaltyFirstReviewReminderSilver:
        case EEventName.LoyaltyMaintainedGold:
        case EEventName.LoyaltyMaintainedPlatinum:
        case EEventName.LoyaltyReviewed:
        case EEventName.LoyaltySecondReviewReminderGold:
        case EEventName.LoyaltySecondReviewReminderPlatinum:
        case EEventName.LoyaltySecondReviewReminderSilver:
            return {
                isSupport: false,
                isTransaction: false,
                actions: [
                    {
                        label: 'My points',
                        onClick: () => navigate(`${routes.my_loyalty_points}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.InviteAccepted:
            return {
                isSupport: false,
                isTransaction: false,
                actions: [
                    {
                        label: 'Go to My points',
                        onClick: () => navigate(`${routes.my_loyalty_points}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.EFTDepostFailed:
        case EEventName.EPSDepositFailed:
        case EEventName.ETransferFaild:
        case EEventName.ScheduleBillLimits:
        case EEventName.ScheduleTransferLimits:
        case EEventName.ScheduleMoneyRequestLimits:
        case EEventName.ScheduleEFTLimits:
        case EEventName.ScheduleEFTDepositLimits:
        case EEventName.ScheduleEPSDepositLimits:
        case EEventName.RefundApproved:
        case EEventName.MoneyRequestApproved:
        case EEventName.MoneyRequestDeclined:
        case EEventName.MoneyRequestReceived:
        case EEventName.ETransferRequestProcessed:
        case EEventName.ETransferRequestDeclined:
            return {
                isSupport: false,
                isTransaction: true,
                actions: []
            };
        case EEventName.ScheduleBillInsufficient:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Load balance',
                        onClick: () => navigate(`${routes.send_and_request_request}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.ScheduleEFTInsufficient:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Load balance',
                        onClick: () => navigate(`${routes.send_and_request_request}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.ScheduleTransferInsufficient:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'Load balance',
                        onClick: () => navigate(`${routes.send_and_request_request}`),
                        variant: 'primary'
                    }
                ]
            };
        case EEventName.GiftCardCancelationApproved:
        case EEventName.GiftCardCancelationDeclined:
            return {
                isSupport: false,
                actions: [
                    {
                        label: 'View gift card',
                        onClick: () =>
                            navigate(
                                `${routes.gift_cards}?id=${notification?.payload?.gift_card_id}`
                            ),
                        variant: 'primary'
                    }
                ]
            };
        default:
            return {
                isSupport: false,
                actions: []
            };
    }
};

const executeNotificationIcon = (notificationCategory?: ENotificationCategory) => {
    switch (notificationCategory) {
        case ENotificationCategory.CARD:
            return 'credit_card';
        case ENotificationCategory.SCHEDULE:
            return 'schedule';
        case ENotificationCategory.PAYMENTS:
            return 'money';
        case ENotificationCategory.MARKETING:
            return 'marketing';
        case ENotificationCategory.PERSONAL:
            return 'person';
        case ENotificationCategory.LOYALTY:
            return 'loyalty';
        default:
            return 'warning';
    }
};

interface INotificationsData {
    notification: TNotification;
    dateLocale: (val: string) => string;
    navigate: NavigateFunction;
    handleGetVerificationResult: (codes: string[], status: EUserStatus) => void;
}

export const executeNotificationsData = ({
    notification,
    handleGetVerificationResult,
    navigate,
    dateLocale
}: INotificationsData) => {
    const notificationCategory = getNotificationGroupByEvent(notification.event);
    return {
        category: notificationCategory,
        actions: getNotificationAction(notification, navigate, handleGetVerificationResult),
        date: prettifyDate(notification.created_at, dateLocale),
        icon: executeNotificationIcon(notificationCategory) as TIconType
    };
};
